import $ from 'jquery';
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import RefineResultsButton from './common/RefineResultsButton';
import OrderListExportButton from './common/OrderListExportButton';
import TrackingNumber from './common/TrackingNumber';
import ReactTableWrapper from './ReactTableWrapper';
import SectionPagination from './SectionPagination';
import OrderListRefineResults from './OrderListRefineResults';
import OrderViewModal from '../containers/OrderViewModalContainer';
import useManualReactTableState from './ManualReactTableState';
import {
    convertDataToListingDisplay,
    convertOrderListingToAReadableFormat,
    getGraphQLTotalRecords,
} from '../selectors/converterSelector';
import { axiosRequestOrders, axiosUpdateOrder } from '../actions/orderActions';
import LocalizedMessage from 'react-l10n';
import { breakpoints, getBreakpoint } from '../dom/_site';
import { handleAxiosErrors, currencyDisplay, currencyObject } from '../_utils';
import { createAlert } from '../dom/window';
import { convertPaymentMethodToAReadableFormat } from '../selectors/converterSelector';

/**
 * Available callback:
 * - applyFilterCallback(filterData) : filterData
 * - updateViewOrderParamsCallback(listParams) : listParams
 *
 * @param props
 * @returns {*}
 * @constructor
 */
function OrderList(props) {
    let mainTableWrapper;

    const {
        className,
        heading,
        callback,
        requestPropWatcher = '',
        memberType,
        sortBy = 'ID',
        sortDirection = 'DESC',
        showStatus = 'Status',
        columnsToExportMap = [
            { key: 'InternalItemID', column: 'Order Number/ID' },
            { key: 'PlacedNice', column: 'Date' },
            { key: 'Status', column: 'Status' },
            { key: 'Total', column: 'Total' },
            { key: 'PaymentMethod', column: 'Payment Method' }
        ],
    } = props;
    const refineResultsModalKey =
        props.refineResultsModalKey || 'refine-results-order-list';
    const viewOrderModalKey =
        props.viewOrderModalKey || 'view-order-order-list';

    // State management
    // --------------------
    const mrtState = useManualReactTableState({
        listParams: {
            sortBy,
            sortDirection,
        },
    });

    // Core
    const { isLoading, toggleLoading } = mrtState;
    const {
        list,
        listParams,
        getDisplayedList,
        setListDisplay,
        updateList,
    } = mrtState;
    const { getFilters, setFilters } = mrtState;
    const {
        getTotalRecords,
        setTotalRecords,
        getActivePage,
        setActivePage,
        getTotalPages,
        getPaginationPages,
    } = mrtState;
    const { handleOnTableUpdate } = mrtState;
    const listEffectWatcher = mrtState.getEffectWatcher();

    // User Input
    // const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    // const { userInput, clearUserInput, updateUserInput } = mrtState;

    // To display Order ID
    const [displayedOrderID, setDisplayedOrderID] = useState(0);

    // Effects
    // --------------------
    // - Main Request
    useEffect(() => {
        request();
    }, [listEffectWatcher, requestPropWatcher]);

    // Functions
    // --------------------
    /**
     * Request List
     */
    const request = () => {
        toggleLoading('list', true);

        // Callback: updateListParam()
        let updatedListParams = JSON.parse(JSON.stringify(listParams));
        if (
            typeof callback != 'undefined' &&
            typeof callback.updateListParams != 'undefined' &&
            callback.updateListParams
        ) {
            updatedListParams = callback.updateListParams(updatedListParams);
        }

        return axiosRequestOrders(updatedListParams)
            .then((response) => {
                const list = convertOrderListingToAReadableFormat(
                    response.data.data.readCartOrders
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data.readCartOrders,
                    'ID'
                );
                const total = getGraphQLTotalRecords(
                    response.data.data.readCartOrders
                );

                console.log('list', list);

                // wait for async requests
                Promise.all([
                    setListDisplay(listingDisplay),
                    updateList(list),
                    setTotalRecords(total),
                ]).then(() => {
                    toggleLoading('list', false);
                });
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleLoading('list', false);
            });
    };


    /**
     * Export Data in CSV
     */
    const exportCSV = async () => {
        let updatedListParams = JSON.parse(JSON.stringify(listParams));
        if (
            typeof callback !== 'undefined' &&
            typeof callback.updateListParams !== 'undefined' &&
            callback.updateListParams
        ) {
            updatedListParams = callback.updateListParams(updatedListParams);
        }

        console.log('Params', updatedListParams);

        const perPage = 1000;
        let activePage = 0;
        let totalCount = 0;
        let allRecords = [];

        try {
            do {
                activePage++;
                const pagedParams = { ...updatedListParams, perPage: perPage, activePage: activePage };
                const response = await axiosRequestOrders(pagedParams);
                const result = response.data.data.readCartOrders;
                const records = result?.edges || [];
                totalCount = result.pageInfo.totalCount;
                allRecords = allRecords.concat(records);
            } while ((perPage * activePage) < totalCount);

            const data = convertOrderListingToAReadableFormat({ edges: allRecords });

            const headers = columnsToExportMap.map(col =>
                typeof col.column === 'string' ? col.column : col.key
            );

            const keys = columnsToExportMap.map(col => col.key);

            const csv = [
                headers.join(','), // Header row
                ...Object.values(data).map(row =>
                    keys.map(key => {
                        let value = row[key];

                        if (key === 'PaymentMethod') {
                            value = convertPaymentMethodToAReadableFormat(value);
                        }

                        if (['ParentMember', 'ORDERED_BY'].includes(key)) {
                            value = value?.BusinessName || (value?.FirstName + ' ' + value?.Surname);
                        }

                        if (typeof value === 'object' && value !== null) {
                            return `"${value.FullName || value.BusinessName || ''}"`;
                        }

                        if (typeof value === 'string') {
                            return `"${value.replace(/"/g, '""')}"`;
                        }

                        return value ?? '';
                    }).join(',')
                )
            ].join('\n');

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `export_${Date.now()}.csv`;
            a.click();
            URL.revokeObjectURL(url);

            createAlert({ message: 'CSV file is downloaded successfully.' }, 'success');

        } catch (error) {
            handleAxiosErrors(error);
        }
    };



    /**
     * Set filter
     *
     * @param additionalFilterData
     */
    const applyFilter = (additionalFilterData) => {
        let updatedFilterData = {
            ...getFilters(),
        };

        if (
            typeof additionalFilterData != 'undefined' &&
            additionalFilterData
        ) {
            updatedFilterData = {
                ...updatedFilterData,
                ...additionalFilterData,
            };
        }

        setFilters(updatedFilterData);
        toggleRefineResultsModal(false);
    };

    /**
     * Toggle the refine results modal
     *
     * @param value
     */
    const toggleRefineResultsModal = (value) => {
        if (value) {
            $('#' + refineResultsModalKey).modal('show');
        } else {
            $('#' + refineResultsModalKey).modal('hide');
        }
    };

    /**
     * Toggle the view order modal
     *
     * @param value
     */
    const toggleViewOrderModal = (value) => {
        if (value) {
            $('#' + viewOrderModalKey).modal('show');
        } else {
            $('#' + viewOrderModalKey).modal('hide');
        }
    };

    const loadOrder = (orderNumber) => {
        if (typeof props.loadOrder != 'undefined') {
            const passedParams = {
                toggleViewOrderModal: toggleViewOrderModal,
                setDisplayedOrderID: setDisplayedOrderID,
                orderNumber: orderNumber,
            };

            props.loadOrder(passedParams);
        } else {
            toggleViewOrderModal(true);
            setDisplayedOrderID(orderNumber);
        }
    };

    /**
     * Get order detail link
     *
     * @param row
     * @returns {*}
     */
    const getOrderDetailLink = (row) => {
        if (props.getOrderDetailLink) {
            return props.getOrderDetailLink(row);
        }

        return `${window.baseURL}checkout/confirmation/${row.original.ID}/?type=orders`;
    };

    /**
     * Get table props (for React Table)
     *
     * @returns {{list: {SubComponent: (function(*): *), columns: *[], showPagination: boolean, manual: boolean, getTrGroupProps: (function(*, *, *, *): {className: string})}}}
     */
    const getTableProps = () => {
        let tableProps = {
            list: {
                manual: true,
                // noDataText: <span>No items found. <br/>See results from our full range below.</span>,
                columns: [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_NUMBER'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'ORDER_NO'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DATE'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PlacedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Placed',
                    },
                    // {
                    //     Header: () => {
                    //         return (
                    //             <React.Fragment>
                    //                 <span className="react-table__heading hidden-xs"><LocalizedMessage id={'ORDER_STATUS'} /></span>
                    //                 <span className="react-table__heading visible-xs"><LocalizedMessage id={'STATUS'} /></span>
                    //                 <span className="react-table__sort-icon"></span>
                    //             </React.Fragment>
                    //         );
                    //     },
                    //     Cell: (row) => {
                    //         return (
                    //             <React.Fragment>
                    //                 <span className="react-table__info">
                    //                     {row.value}
                    //                 </span>
                    //             </React.Fragment>
                    //         );
                    //     },
                    //     minWidth: undefined,
                    //     accessor: 'Status',
                    // },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: showStatus,
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        {memberType == 'wholesaler' && (
                                            <LocalizedMessage id={'SUPPLIER'} />
                                        )}
                                        {(memberType == 'reseller' || memberType == 'parent-reseller') && (
                                            <LocalizedMessage id={'WHOLESALER'} />
                                        )}
                                        {memberType == 'retail-customer' && (
                                            <LocalizedMessage id={'RESELLER'} />
                                        )}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { Supplier } = row.original;

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {Supplier != null &&
                                            (Supplier.BusinessName ||
                                                Supplier.FullName)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ID',
                        show: memberType == 'wholesaler' ? false : true,
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TOTAL'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { CurrencyDisplayCode, CurrencyFormat } = row.original;
                            const { locale } = currencyObject;

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {currencyDisplay(row.value, CurrencyDisplayCode, CurrencyFormat, locale)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'Total',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage
                                            id={'PAYMENT_METHOD'}
                                        />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PaymentMethod == 'order-card-authorization-payfast' ? "PayFast" : row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        sortable: false,
                        accessor: 'PaymentMethodNice',
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ],
                showPagination: false,
                getTrGroupProps: (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];
                    let classes =
                        'react-table__row react-table__row--selectable';
                    classes += ` ${isExpanded ? 'react-table__row--expanded' : ''
                        }`;

                    return {
                        className: classes,
                        onRowClick: (e) => {
                            if ($(window).width() >= breakpoints.md) {
                                // loadOrder(row.original.ID);
                                window.location.href = getOrderDetailLink(row);
                            }
                        },
                    };
                },
                SubComponent: (row) => {
                    const { Supplier, Total, ID, CurrencyDisplayCode, CurrencyFormat } = row.original;
                    const { locale } = currencyObject;

                    return (
                        <div className='react-table__extra-data'>
                            <div className='row'>
                                <div className='col-xs-4 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        {memberType == 'wholesaler' && (
                                            <LocalizedMessage id={'SUPPLIER'} />
                                        )}
                                        {(memberType == 'reseller' ||
                                            memberType ==
                                            'parent-reseller') && (
                                                <LocalizedMessage
                                                    id={'WHOLESALER'}
                                                />
                                            )}
                                        {memberType == 'retail-customer' && (
                                            <LocalizedMessage id={'RESELLER'} />
                                        )}
                                    </div>
                                    <div className='react-table__info'>
                                        {Supplier != null && Supplier.FullName}
                                    </div>
                                </div>
                                <div className='col-xs-4 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'TOTAL'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {currencyDisplay(Total, CurrencyDisplayCode, CurrencyFormat, locale)}
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-xs-12 vspace vspace--small'>
                                    <div className='data-table__heading'>
                                        <LocalizedMessage id={'VIEW'} />
                                    </div>
                                    <div className='data-table__info'>
                                        <a
                                            href={`${window.baseURL
                                                }checkout/confirmation/${ID}/`}
                                            className='button button--grey-border'
                                        >
                                            <LocalizedMessage
                                                id={'VIEW_DETAILS'}
                                            />
                                        </a>
                                        {/*<button type="button" className="button button--grey-border" onClick={() => loadOrder(ID)}>*/}
                                        {/*<LocalizedMessage id={'VIEW_DETAILS'} />*/}
                                        {/*</button>*/}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        };

        console.log('tableProps Before Hook: ', tableProps);
        if (
            typeof callback != 'undefined' &&
            typeof callback.updateTableProps != 'undefined' &&
            callback.updateTableProps
        ) {
            tableProps = callback.updateTableProps(tableProps, {
                loadOrder,
                toggleViewOrderModal,
                toggleRefineResultsModal,
            });
            console.log('tableProps After Hook: ', tableProps);
        }

        return tableProps;
    };

    return (
        <React.Fragment>
            <div className={classnames('card card--border', className)}>
                <div className='card__header'>
                    <div className='row row--vcenter-md'>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <h2 className='heading heading--lv4'>
                                {heading || 'Your Recent Orders'}
                            </h2>
                        </div>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <div className='text-box--right'>
                                <OrderListExportButton
                                    onClick={() =>
                                        exportCSV()
                                    }
                                />
                                <RefineResultsButton
                                    onClick={() =>
                                        toggleRefineResultsModal(true)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <ReactTableWrapper
                    key={getBreakpoint()}
                    loading={isLoading.list}
                    onFetchData={handleOnTableUpdate}
                    data={getDisplayedList()}
                    reactTable={getTableProps()}
                    ref={(r) => (mainTableWrapper = r)}
                />
            </div>
            <SectionPagination
                activePage={getActivePage()}
                totalPages={getTotalPages()}
                pages={getPaginationPages()}
                onPaginationClick={(pageNum) => setActivePage(pageNum)}
            />
            <OrderListRefineResults
                modalKey={refineResultsModalKey}
                activeFilters={getFilters()}
                applyFilter={applyFilter}
            />
            <OrderViewModal
                id={displayedOrderID}
                modalKey={viewOrderModalKey}
                updateListParamsCallback={
                    typeof callback != 'undefined' &&
                        typeof callback.updateViewOrderParamsCallback
                        ? callback.updateViewOrderParamsCallback
                        : null
                }
            />
        </React.Fragment>
    );
}

export default OrderList;
